import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  debounce,
  IconButton,
  LinearProgress,
  Modal,
  Pagination,
  TableContainer,
  TextField,
} from "@mui/material";
import { useQuery } from "react-query";
import { useMutation } from "react-query";
import { getAllHistory } from "../../api/calls-history/calls-history";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { api } from "../../api/Intercepter";
import HistoryAutoComplete from "./history-auto-complete/history-auto-complete";
import { CustomTextField } from "../../components/inputs/input/BootStrapInput";
import { DateRange } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { Fade, Menu, ThemeProvider, createTheme } from "@mui/material";
import { StyledTextarea } from "../../components/inputs/custom-text-area/custom-text-area";
import { formatDateAndTime } from "../../utils/formatDateAndTime";
import { convertArabicToEnglishNumber } from "../../utils/convertArabicToEnglish";
import EntreatyButton from "./entreaty-button/entreaty-button";
import RadioShowFilter from "./radio-show-filter/radio-show-filter";
import DateRangePickerMui from "../../components/inputs/date-range-picker-mui/DateRangePickerMui";
import BasicRangeShortcuts from "../../components/inputs/date-range-picker-mui/CustomShortcutItem";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddParticipationModal from "../../components/add-participation-modal/add-participation-modal";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { Tooltip } from "@mui/material";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  border: "none",
  borderRadius: "6px",
};

const INITIAL_DATE_RANGE: DateRange<Dayjs> = [
  dayjs().subtract(1, "day").add(3, "hours"),
  dayjs().add(3, "hours"),
];

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#3182ce" }, // A nice blue for the accent
    background: { paper: "#2d3748" }, // Dark background for the picker
    text: { primary: "#ffffff", secondary: "#a0aec0" },
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // Styles for shortcut buttons
          "&.MuiPickersShortcuts-button": {
            color: "#ffffff",
            backgroundColor: "#4a5568",
            "&:hover": {
              backgroundColor: "#2d3748",
            },
          },
        },
      },
    },
  },
});

const History = () => {
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const limit: number = 100;

  const [radioShow, setRadioShow] = useState("");
  const [starredRows, setStarredRows] = useState<Set<number>>(new Set());

  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editedFullName, setEditedFullName] = useState("");
  const [editedAutoComplete, setEditedAutoComplete] = useState<any>();
  const [editedNotes, setEditedNotes] = useState("");
  const [clientHistoryId, setClientHistoryId] = useState("");
  const [startDate, setStartDate] = useState<any>(
    INITIAL_DATE_RANGE[0].toISOString()
  );
  const [endDate, setEndDate] = useState<any>(
    INITIAL_DATE_RANGE[1].toISOString()
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openDateRange = Boolean(anchorEl);

  const handleSearch = (value) => {
    let cleanedPhoneNumber = convertArabicToEnglishNumber(value ?? 0)?.replace(
      /^07/,
      "7"
    );
    console.log(cleanedPhoneNumber.replace(/^(\+964|964)/, ""));

    return cleanedPhoneNumber.replace(/^(\+964|964)/, "");
  };

  const {
    data: historyData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [
      `history-page-${page}-limit-${limit}-${search}-${startDate}-${endDate}-${radioShow}`,
    ],
    queryFn: async () => {
      const data = await getAllHistory({
        currentPage: page,
        limit: limit,
        search: handleSearch(search),
        startDate: startDate,
        endDate: endDate,
        radioShow: radioShow,
      });
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const { data: labelsData } = useQuery({
    queryKey: ["labels"],
    queryFn: async () => {
      const response = await api.get("/labels");
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (historyData?.array) {
      const starredClientIds = historyData.array
        .filter((row) => row.labels)
        .map((row) => row.clientid);
      setStarredRows(new Set(starredClientIds));
      console.log(
        "Initialized starred rows from history data:",
        starredClientIds
      );
    }
  }, [historyData]);

  const { mutateAsync: toggleClientLabel } = useMutation({
    mutationFn: async ({
      clientId,
      labelId,
      action,
    }: {
      clientId: number;
      labelId: number;
      action: "add" | "remove";
    }) => {
      if (action === "add") {
        const response = await api.post("/client-labels", {
          clientId,
          labelId,
        });
        console.log("POST response:", response.data);
        return response.data;
      } else {
        const response = await api.delete(
          `/client-labels/${clientId}/${labelId}`
        );
        console.log("DELETE response:", response.data);
        return response.data;
      }
    },
    onSuccess: (data) => {
      console.log("Mutation successful:", data);
      refetch();
    },
    onError: (error) => {
      console.error("Mutation failed:", error);
    },
  });

  useEffect(() => {
    refetch();
  }, []);

  const handleDoubleClick = (index, fullName, notes) => {
    setEditingRowIndex(index);
    setEditedFullName(fullName);
  };

  const handleFullNameChange = (e) => {
    setEditedFullName(e.target.value);
  };

  const handleNotesChange = (e) => {
    setEditedNotes(e.target.value);
  };

  const handleSubmitData = (index, userId) => {
    api
      .patch(`${import.meta.env.VITE_SOME_KEY_BASE_URL}/clients/${userId}`, {
        fullName: editedFullName,
        neighborhoodId: editedAutoComplete.neighborhood_id ?? undefined,
        cityId: editedAutoComplete.city_id ?? undefined,
        subCityId: editedAutoComplete.sub_city_id ?? undefined,
        governorateId: editedAutoComplete.governorate_id ?? undefined,
      })
      .then(() => {
        refetch();
      });

    setEditedAutoComplete("");
    setEditingRowIndex(null);
    setEditedFullName("");
  };

  const handleSubmitNotes = () => {
    api
      .patch(
        `${import.meta.env.VITE_SOME_KEY_BASE_URL}/calls/${clientHistoryId}`,
        {
          notes: editedNotes,
        }
      )
      .then((response) => {
        refetch();
      });

    setClientHistoryId("");
    handleCloseNotesModal();
  };

  function formatTime(seconds) {
    if (isNaN(seconds) || seconds < 0) {
      return "Invalid input";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const formattedTime = `${minutes}:${
      remainingSeconds < 10 ? "0" : ""
    }${remainingSeconds}`;

    return formattedTime;
  }

  const handleDateRange = (e: any) => {
    setStartDate(e[0]?.toISOString());
    setEndDate(e[1]?.toISOString());
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleStar = async (clientId: number) => {
    try {
      if (!labelsData?.array || labelsData.array.length === 0) {
        console.error("Labels data not loaded yet or empty");
        return;
      }

      const starLabel = labelsData.array[0];

      if (!starLabel) {
        console.error("No labels available");
        return;
      }

      // Find the current row to check its labels field
      const currentRow = historyData?.array?.find(
        (row) => row.clientid === clientId
      );
      const isCurrentlyStarred = currentRow?.labels ? true : false;
      const action = isCurrentlyStarred ? "remove" : "add";

      console.log(`Action: ${action} star for client ${clientId}`);
      console.log("Current row labels:", currentRow?.labels);

      await toggleClientLabel({
        clientId: clientId,
        labelId: starLabel.id,
        action: action,
      });
    } catch (error) {}
  };

  const [openNotesModal, setOpenNotesModal] = useState(false);
  const handleCloseNotesModal = () => setOpenNotesModal(false);
  const [openParticipationModal, setOpenParticipationModal] = useState(false);
  const [selectedUserInfo, setSelectedUserInfo] = useState<{
    clientId: number;
    fullName: string;
    phoneNumber: string;
  } | null>(null);

  const handlePagination = useCallback(
    debounce((value) => {
      setPage(Number(value) - 1);
    }, 1000),
    []
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--bg-page)",
        padding: "20px 40px",
      }}
    >
      <Box sx={{ display: "flex", gap: "10px" }}>
        <CustomTextField
          placeholder="البحث"
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            backgroundColor: "var(--card-bg)",
            color: "var(--text-primary)",
            ".MuiInputBase-input": { color: "var(--text-primary)" },
          }}
        />
        <Box sx={{ width: "fit-content" }}>
          <DateRangePickerMui
            startDate={startDate}
            endDate={endDate}
            handleClick={handleClick}
            style={{
              backgroundColor: "var(--card-bg)",
              color: "var(--text-primary)",
            }}
          />
          <Menu
            id="fade-menu"
            MenuListProps={{
              "aria-labelledby": "fade-button",
            }}
            anchorEl={anchorEl}
            open={openDateRange}
            onClose={handleClose}
            TransitionComponent={Fade}
            disablePortal
          >
            <ThemeProvider theme={darkTheme}>
              <BasicRangeShortcuts
                handleDateRange={handleDateRange}
                startDate={startDate}
                endDate={endDate}
              />
            </ThemeProvider>
          </Menu>
        </Box>
        <RadioShowFilter setRadioShow={setRadioShow} />
      </Box>
      <div style={{ backgroundColor: "var(--card-bg)", borderRadius: "12px" }}>
        <div style={{ width: "100%", marginTop: "20px" }}>
          <TableContainer
            sx={{
              width: "100%",
              backgroundColor: "var(--card-bg)",
              paddingBottom: "20px",
              borderRadius: "6px",
            }}
          >
            <Table
              sx={{ minWidth: 650, "*": { fontSize: "14px" } }}
              aria-label="simple table"
            >
              <TableHead sx={{ backgroundColor: "var(--table-head-bg)" }}>
                <TableRow>
                  <TableCell>ت</TableCell>
                  <TableCell>ليبل</TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>رقم الهاتف</TableCell>
                  <TableCell>القضاء</TableCell>
                  <TableCell>المنطقة</TableCell>
                  <TableCell>وقت الاتصال</TableCell>
                  <TableCell>مدة الاتصال</TableCell>
                  <TableCell>البرنامج</TableCell>
                  <TableCell>عدد المكالمات</TableCell>
                  <TableCell>مللاحظات</TableCell>
                  <TableCell>تعديل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "*": { fontSize: "14px !important" } }}>
                {historyData?.array?.map((row, index) => (
                  <TableRow
                    key={row?.id}
                    onDoubleClick={() =>
                      handleDoubleClick(index, row.fullname, row.notes)
                    }
                  >
                    <TableCell sx={{ width: "0%", direction: "rtl" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ width: "0%", textAlign: "center" }}>
                      <IconButton
                        onClick={() => toggleStar(row?.clientid)}
                        disabled={!labelsData?.array}
                        sx={{
                          padding: "4px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                          },
                          "&:disabled": {
                            opacity: 0.5,
                          },
                        }}
                      >
                        {row?.labels ? (
                          <StarIcon
                            sx={{
                              color: "var(--star-active)",
                              fontSize: "30px",
                              filter:
                                "drop-shadow(0 0 3px rgba(255, 215, 0, 0.5))",
                            }}
                          />
                        ) : (
                          <StarBorderIcon
                            sx={{
                              color: "var(--star-inactive)",
                              fontSize: "30px",
                              "&:hover": {
                                color: "var(--star-active)",
                              },
                            }}
                          />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "20%",
                        color:
                          row.reason === "IGNORE" ||
                          row.reason === "NOT ANSWERED"
                            ? "var(--accent)"
                            : "var(--text-primary)",
                      }}
                    >
                      {editingRowIndex === index ? (
                        <TextField
                          type="text"
                          value={editedFullName}
                          onChange={handleFullNameChange}
                          size="small"
                        />
                      ) : (
                        <span>{row?.fullname}</span>
                      )}
                    </TableCell>

                    <TableCell sx={{ width: "0%", direction: "rtl" }}>
                      {row?.phonenumber}
                    </TableCell>
                    <TableCell>
                      {historyData?.locations?.[row?.id]?.city_name ??
                        "لا يوجد"}
                    </TableCell>
                    <TableCell sx={{ width: "30%" }}>
                      {editingRowIndex === index ? (
                        <HistoryAutoComplete
                          data={row}
                          editedAutoComplete={editedAutoComplete}
                          setEditedAutoComplete={setEditedAutoComplete}
                          locations={historyData?.locations}
                        />
                      ) : (
                        <>
                          {historyData?.locations?.[row?.id]
                            ?.neighborhood_name ??
                            historyData?.locations?.[row?.id]?.sub_city_name ??
                            historyData?.locations?.[row?.id]?.city_name ??
                            "لا يوجد"}
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: "15%" }}>
                      {formatDateAndTime(row?.startdate)}
                    </TableCell>
                    <TableCell sx={{ width: "0%" }}>
                      {formatTime(row?.duration)}
                    </TableCell>
                    <TableCell sx={{ width: "10%" }}>
                      {row?.radioshowname}
                    </TableCell>
                    <TableCell sx={{ width: "5%" }}>{row?.callcount}</TableCell>
                    <TableCell
                      sx={{ width: "100%" }}
                      onDoubleClick={() => {
                        setEditedNotes(row?.notes);
                        setClientHistoryId(row?.callid);
                        setOpenNotesModal(true);
                      }}
                    >
                      {row.notes}
                    </TableCell>
                    {editingRowIndex === index ? (
                      <TableCell sx={{ width: "0px" }}>
                        <Button
                          onClick={() => handleSubmitData(index, row?.clientid)}
                        >
                          حفظ
                        </Button>
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <EntreatyButton callId={row?.id} />
                          <Tooltip title="تعديل البيانات">
                            <IconButton
                              onClick={() =>
                                handleDoubleClick(
                                  index,
                                  row.fullname,
                                  row.notes
                                )
                              }
                            >
                              <EditOutlinedIcon
                                sx={{ fontSize: "20px !important" }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="إضافة مشاركة">
                            <IconButton
                              onClick={() => {
                                setSelectedUserInfo({
                                  clientId: Number(row.clientid),
                                  fullName: row.fullname,
                                  phoneNumber: row.phonenumber,
                                });
                                setOpenParticipationModal(true);
                              }}
                            >
                              <CardGiftcardIcon
                                sx={{ fontSize: "20px !important" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {isLoading && (
              <LinearProgress
                color="primary"
                data-testid="product-member-loader"
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10px",
                gap: "10px",
              }}
            >
              <Pagination
                sx={{ marginLeft: "20px" }}
                page={page + 1}
                count={Math.ceil(historyData?.count / limit)}
                onChange={(e, value) => setPage(value - 1)}
                variant="outlined"
                shape="rounded"
                size="medium"
              />
              <TextField
                type="number"
                onChange={(e) => {
                  if (
                    Number(e.target.value) > 0 &&
                    Number(e.target.value) <=
                      Math.ceil(historyData?.count / limit)
                  ) {
                    handlePagination(Number(e.target.value));
                  }
                }}
                defaultValue={page + 1}
                size="small"
                sx={{ width: "100px" }}
              />
            </Box>
          </TableContainer>
        </div>
      </div>
      <Modal
        open={openNotesModal}
        onClose={handleCloseNotesModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...style,
            bgcolor: "var(--bg-paper)",
            color: "var(--text-primary)",
          }}
        >
          <StyledTextarea
            value={editedNotes}
            onChange={handleNotesChange}
            placeholder="ملاحظات"
            sx={{
              backgroundColor: "var(--card-bg)",
              color: "var(--text-primary)",
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <Button
              sx={{
                width: "100%",
                fontSize: "16px",
                padding: "6px 40px",
                color: "var(--tab-active-text)",
                borderRadius: "6px",
                backgroundColor: "var(--accent)",
                "&:hover": {
                  backgroundColor: "var(--accent)",
                  color: "var(--tab-active-text)",
                },
              }}
              onClick={() => handleSubmitNotes()}
            >
              حفظ
            </Button>
          </Box>
        </Box>
      </Modal>
      {selectedUserInfo && (
        <AddParticipationModal
          open={openParticipationModal}
          onClose={() => setOpenParticipationModal(false)}
          onSuccess={() => refetch()}
          userInfo={selectedUserInfo}
          modalType="call"
        />
      )}
    </div>
  );
};

export default History;
